/**
 * -----------------------------------------------------------------------------
 * FleetTag Detail Page
 * -----------------------------------------------------------------------------
 * This page is used to show the details of a single fleettag of the organization.
 */

import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { toZonedTime } from "date-fns-tz";
import { useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import {
  useDeleteSingleFleettagMutation,
  useEditOrganizationFleettagMutation,
  useOrganizationFleettagsQuery,
  useSingleOrganizationFleettagQuery,
} from "../../../api/network/adminApiServices";
import { RESPONSE_CODE } from "../../../api/network/constants";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import {
  TListingQueryParams,
  TModalsState,
  setListingQueryParams,
  setModalsData,
} from "../../../api/store/commonSlice";
import {
  SingleOrganizationFleettagPayload,
  TEditOrganizationFleettagPayloadData,
  TLegacyFleettag,
} from "../../../api/types/Fleettag";
import svgIcons from "../../../assets/svg/svgIcons";
import Accordian from "../../../components/accordian";
import {
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import ModalDeleteConfirmation from "../../../components/admin/modalDeleteConfirmation";
import AdminListingColumnItem from "../../../components/adminListingColumnItem";
import FillAllRequiredFieldsCaption from "../../../components/fillAllRequiredFieldsCaption";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import AppSearchBox from "../../../components/searchBox";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { useAccountTimezone } from "../../../hooks/timezone";
import { routeUrls } from "../../../navigation/routeUrls";
import { dbTimeToJsTime } from "../../../utils/dateTime";
import { useLoggedInUserData } from "../../../utils/user";
import { ROLE_ACCESS_LEVELS } from "../role/constants";
import FleettagDetailForm from "./fleettag-form";
import { TListData } from "./type";
import {
  fleettagDetailsInitialValues,
  fleettagDetailsYupValidationSchema,
} from "./validation";
import { roundOffLatLong } from "../../../utils/map";
import FormActionButtons, {
  FormActionButton,
} from "../../../components/admin/formActionButtons";

const ScreenAdminDetailFleettag = () => {
  const { fleettagId } = useParams<{ fleettagId: any }>();
  const { state: locationState } = useLocation(); // OrganizationEntityListingPayload | { new: true }
  const { t: tMain } = useTranslation();
  const { t: tAdmin } = useTranslation("translation", {
    keyPrefix: "admins.fleettags",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.fleettags.detailsPage",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { getUserACLAccessLevel } = useACL();

  const { convertToTZForDateFns, tzForDateFns } = useAccountTimezone();

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { fleetTags: orgFleettagsQueryParams } = listingQueryParams;

  // flag to idenfiy if fleettag is coming from create new fleettag popup
  const isNewEntity = useRef<boolean>(!!locationState?.new);

  // flag to enable edit mode
  const [userCanEdit, setUserCanEdit] = useState<boolean>(
    !!isNewEntity?.current
  );

  // prepare query params for fetching organization fleettags
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const thisOrgFleetTagAllowed = useLoggedInUserData("hasfleettags");
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        fleetTags: { ...orgFleettagsQueryParams, page: 1, search: value },
      })
    );
  }, 500);

  // fetch organization fleettags
  const {
    data: dataOrgFleettags,
    isFetching: isFetchingOrgFleettags,
    error,
  } = useOrganizationFleettagsQuery(orgFleettagsQueryParams);
  const { results, count } = dataOrgFleettags || {};

  // fetch single fleettag details
  const {
    data: dataSingleFleettag,
    isFetching: isFetchingSingleFleettag,
    isError: isErrorSingleEntity,
    error: errorSingleEntity,
  } = useSingleOrganizationFleettagQuery(
    {
      account_id: thisUserOrganizationId,
      entity_id: fleettagId,
    } as SingleOrganizationFleettagPayload,
    {
      skip: !fleettagId,
      refetchOnMountOrArgChange: true, // prevent cached values from displaying when creating entity with same id
    }
  );
  useEffect(() => {
    if (isErrorSingleEntity) {
      if (
        (errorSingleEntity as FetchBaseQueryError)?.status ===
        RESPONSE_CODE.NOT_FOUND
      ) {
        toast.error(t("toast.fleettag_not_found"));
        navigate(routeUrls.dashboardChildren.adminChildren.fleettags);
      }
    }
  }, [isErrorSingleEntity, errorSingleEntity]);

  // fleettag mutations
  const [
    editOrganizationFleettagApiTrigger,
    { isLoading: isLoadingEditFleettag },
  ] = useEditOrganizationFleettagMutation();
  const [
    deleteSingleFleettagApiTrigger,
    { isLoading: isLoadingDeleteFleettag },
  ] = useDeleteSingleFleettagMutation();

  // prepare list data for fleettag list
  const listData: TListData[] = !!results
    ? (results || ([] as TLegacyFleettag[])).map(
        (item: TLegacyFleettag, index: number) => ({
          id: item?.entityid || "-",
          fleettag_id: item?.entityid || "-",
          name: item?.description || "-",
          last_address: item?.address || "-",
          in_range: item?.isattached === 1 ? "Yes" : "No",
        })
      )
    : [];

  // formik
  const formik = useFormik({
    initialValues: fleettagDetailsInitialValues,
    validationSchema: fleettagDetailsYupValidationSchema,
    onSubmit: (values) => {
      // prepare payload
      const data: TEditOrganizationFleettagPayloadData = {
        accountid: thisUserOrganizationId,
        entityid: values.fleet_tag_id,
        description: values.fleet_tag_name,

        // rest of the fields cannot be edited
        // lastupdatetime: dataSingleFleettag?.lastupdatetime || 0,
        // statuscode: values.last_status_code,
        // isattached: values.in_range === "Yes" ? 1 : 0, //TODO in_range is not in response
        // deviceid: values.in_range_device_id, //TODO deviceid is not in response
        // last_location: values.last_location, //TODO last_location is not in response discuss with hemant how to send latitute and longitude
        // address: values.last_address,
        // altitude: values.last_altitude,
        // deviceodomkm: values.distance_travelled,
        // tagsigstrength: values.tag_signal_strength,
        // tagbattlevel: values.tag_battery_level,
        // tagtempc: values.temprature,
      };

      // call api for updating fleettag
      editOrganizationFleettagApiTrigger({ data })
        .unwrap()
        .then(() => {
          toast.success(t("toast.fleettag_updated"));
          navigate(routeUrls.dashboardChildren.adminChildren.fleettags);
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.update_failed")];
          toast.error(errors?.join(" "));
        });
    },
  });

  // pre-fill formik values
  useEffect(() => {
    if (dataSingleFleettag) {
      formik.setValues({
        fleet_tag_id: dataSingleFleettag?.entityid || "",
        fleet_tag_name: dataSingleFleettag?.description || "", //TODO name is not in response
        last_event_time: toZonedTime(
          dbTimeToJsTime(dataSingleFleettag?.lastupdatetime || 0),
          convertToTZForDateFns(tzForDateFns)
        )
          .toISOString()
          .replace(".000Z", "")
          .split("T")
          .join(" "),
        last_status_code:
          dataSingleFleettag?.status_descr ||
          `${dataSingleFleettag?.statuscode}`,
        in_range: dataSingleFleettag?.isattached === 1 ? "Yes" : "No",
        in_range_device_id: dataSingleFleettag?.deviceid || "",
        last_location:
          `${roundOffLatLong(dataSingleFleettag?.latitude ?? 0)} , ${roundOffLatLong(dataSingleFleettag?.longitude ?? 0)}` ||
          "0",
        // last_location:
        //   dataSingleFleettag?.latitude + dataSingleFleettag?.longitude || 0, //TODO last_location is not in response
        last_address: dataSingleFleettag?.address || "",
        last_altitude: dataSingleFleettag?.altitude || 0,
        distance_travelled: dataSingleFleettag?.deviceodomkm || 0, //TODO distance_travelled is not in response
        tag_signal_strength: dataSingleFleettag?.tagsigstrength || 0,
        tag_battery_level: dataSingleFleettag?.tagbattlevel || 0,
        temprature: dataSingleFleettag?.tagtempc || 0,
      });
      setUserCanEdit(!!isNewEntity?.current);
    }
  }, [dataSingleFleettag, fleettagId]);

  const { values, errors, touched, handleChange, handleBlur, handleSubmit } =
    formik;

  // handle edit fleettag
  const handleEditFleettag = () => {
    if (userCanEdit) {
      formik.handleSubmit();
    }
  };

  // handle delete fleettag
  const handleDeleteFleettag = () => {
    deleteSingleFleettagApiTrigger({
      account_id: `${thisUserOrganizationId}`,
      entity_id: fleettagId,
    })
      .unwrap()
      .then(() => {
        toast.success(t("toast.fleettag_deleted"));
        navigate(routeUrls.dashboardChildren.adminChildren.fleettags);
      })
      .catch((error) => {
        const errors = !!error?.data
          ? serializeErrorKeyValues(error?.data)
          : [t("toast.deletion_failed")];
        toast.error(errors?.join(" "));
      });
  };

  const onBackButtonClick = () =>
    navigate(routeUrls.dashboardChildren.adminChildren.fleettags);

  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            ...(thisOrgFleetTagAllowed === "1"
              ? [
                  {
                    customWrapperClass: "w-24",
                    type: "button",
                    label: tMain("delete"),
                    variant: "danger",
                    onClick: () => {
                      dispatch(
                        setModalsData({
                          ...modalsState,
                          showDeleteConfirmation: true,
                        })
                      );
                    },
                    disabled:
                      isFetchingSingleFleettag ||
                      isLoadingEditFleettag ||
                      getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_FLEETTAG) <
                        ROLE_ACCESS_LEVELS.ALL,
                  } as FormActionButton,
                ]
              : []),

            ...(userCanEdit
              ? [
                  {
                    customWrapperClass: "w-24",
                    type: "button",
                    label: tMain("cancel"),
                    onClick: () => onBackButtonClick(),
                    variant: "secondary",
                  } as FormActionButton,
                ]
              : []),

            ...(thisOrgFleetTagAllowed === "1"
              ? [
                  {
                    customWrapperClass: "w-24",
                    type: "button",
                    variant: "primary",
                    label: userCanEdit ? tMain("update") : tMain("edit"),
                    onClick: userCanEdit
                      ? handleEditFleettag
                      : () => setUserCanEdit(!userCanEdit),
                    disabled:
                      isFetchingSingleFleettag ||
                      isLoadingEditFleettag ||
                      (userCanEdit && !!Object.keys(errors).length) ||
                      getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_FLEETTAG) <
                        ROLE_ACCESS_LEVELS.WRITE,
                  } as FormActionButton,
                ]
              : []),
          ]}
        />
      </>
    );
  }, [
    modalsState,
    isFetchingSingleFleettag,
    isLoadingEditFleettag,
    userCanEdit,
    errors,
  ]);
  const MemoizedFormActionSaveButton = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              type: "submit",
              variant: "success",
              label: tMain("save"),
              onClick: () => handleEditFleettag(),
              disabled: isLoadingEditFleettag || !!Object.keys(errors).length,
            },
          ]}
        />
      </>
    );
  }, [isLoadingEditFleettag, errors]);
  return (
    <>
      <HeaderView
        title={t("heading")}
        showBackButton={true}
        backButtonCallback={onBackButtonClick}
      />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}>
        <div className="lg:grid lg:grid-cols-12 mt-8 py-2 gap-4">
          <div
            className={`hidden xl:block lg:col-span-3 space-y-4${isFetchingOrgFleettags ? " opacity-40 pointer-events-none" : ""}${isNewEntity?.current ? " xl:hidden" : ""}`}
          >
            <div className="font-bold text-lg leading-6 mt-2 mb-3">
              {t("listing_heading")}
            </div>
            <AppSearchBox
              placeholder={tAdmin("search_placeholder")}
              initialValue={orgFleettagsQueryParams.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                debouncedSetSearchKeyword(e.target.value)
              }
              onClear={() => {
                debouncedSetSearchKeyword("");
              }}
            />
            <div className="max-h-admins overflow-y-auto">
              {listData?.map((item: any, index: number) => (
                <AdminListingColumnItem
                  key={index}
                  selected={fleettagId === item.id}
                  onClick={() =>
                    navigate(
                      `${routeUrls.dashboardChildren.adminChildren.fleettags}/${item.id}`
                    )
                  }
                  title={item.fleettag_id}
                  description={item.last_address}
                  asideText={item.in_range}
                  bottomText={item.name}
                />
              ))}
            </div>
            {!isFetchingOrgFleettags && (
              <Pagination
                pageSize={orgFleettagsQueryParams.page_size}
                handlePageSizeChange={(e: TSelectboxOption | null) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      fleetTags: {
                        ...orgFleettagsQueryParams,
                        page: 1,
                        page_size: parseInt(`${e?.value}`),
                      },
                    })
                  );
                }}
                totalPages={
                  count
                    ? Math.ceil(count / orgFleettagsQueryParams.page_size)
                    : 1
                }
                forcePage={orgFleettagsQueryParams.page - 1}
                handlePageClick={(data: TPaginationSelected) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      fleetTags: {
                        ...orgFleettagsQueryParams,
                        page: data?.selected + 1,
                      },
                    })
                  );
                }}
                onlyPageChange={true}
                size={1}
              />
            )}
          </div>
          <div
            className={`${isNewEntity?.current ? "lg:col-span-12" : "col-span-12 xl:col-span-9"}`}
          >
            <div className="flex items-center gap-4">
              {isNewEntity?.current ? (
                <>
                  <p className="font-semibold text-accent-blue-dark text-base leading-6">
                    {tMain("admins.completeCreation")}
                  </p>
                  <div className="flex-grow"></div>
                  {isFetchingSingleFleettag && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  <div className="w-24">
                    {/* <AdminFormFieldSubmit
                      type="submit"
                      variant="success"
                      label={tMain("save")}
                      onClick={handleEditFleettag}
                      disabled={
                        isLoadingEditFleettag || !!Object.keys(errors).length
                      }
                    /> */}
                    {MemoizedFormActionSaveButton}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-grow"></div>
                  {isFetchingSingleFleettag && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  {/* <div className="w-24">
                    {thisOrgFleetTagAllowed === "1" && (
                      <AdminFormFieldSubmit
                        type="button"
                        variant="danger"
                        label={tMain("delete")}
                        onClick={() => {
                          dispatch(
                            setModalsData({
                              ...modalsState,
                              showDeleteConfirmation: true,
                            })
                          );
                        }}
                        disabled={
                          isFetchingSingleFleettag ||
                          isLoadingEditFleettag ||
                          getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_FLEETTAG) <
                            ROLE_ACCESS_LEVELS.ALL
                        }
                      />
                    )}
                  </div> */}
                  {/* {userCanEdit && (
                    <div className="w-24">
                      <AdminFormFieldSubmit
                        type="button"
                        variant="secondary"
                        label={tMain("cancel")}
                        onClick={onBackButtonClick}
                      />
                    </div>
                  )} */}
                  {/* <div className="w-24">
                    {thisOrgFleetTagAllowed === "1" && (
                      <AdminFormFieldSubmit
                        type="button"
                        variant="primary"
                        label={userCanEdit ? tMain("update") : tMain("edit")}
                        onClick={
                          userCanEdit
                            ? handleEditFleettag
                            : () => setUserCanEdit(!userCanEdit)
                        }
                        disabled={
                          isFetchingSingleFleettag ||
                          isLoadingEditFleettag ||
                          (userCanEdit && !!Object.keys(errors).length) ||
                          getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_FLEETTAG) <
                            ROLE_ACCESS_LEVELS.WRITE
                        }
                      />
                    )}
                  </div> */}
                  {MemoizedFormActionButtons}
                </>
              )}
            </div>
            <div
              className={`rounded-lg mt-2 bg-accent-blue-pale transition ${isFetchingSingleFleettag || isLoadingEditFleettag || isLoadingDeleteFleettag ? "opacity-40" : ""}`}
            >
              <form onSubmit={handleSubmit}>
                <Accordian title={t("accord_details")} openByDefault={true}>
                  <FleettagDetailForm
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    formikSetValue={formik.setFieldValue}
                    handleBlur={handleBlur}
                    formikSetTouched={formik.setFieldTouched}
                    userCanEdit={userCanEdit}
                  />
                </Accordian>
              </form>
            </div>
          </div>
        </div>
      </div>
      <ModalDeleteConfirmation handleDeleteAdmin={handleDeleteFleettag} />
    </>
  );
};

export default ScreenAdminDetailFleettag;
